import type { GetAnalyticsPayslipsProcessedListParams } from '@nxsys/types/api/analytics';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useGetEmployeeStatus } from '@nxsys/hooks/api/analytics/useGetEmployeeStatus';
import { useGetPaymentsTaxPeriod } from '@nxsys/hooks/api/analytics/useGetPaymentsTaxPeriod';

export type DashboardEmployerAnalyticsContextValue = {
  params?: GetAnalyticsPayslipsProcessedListParams;
  setParams: React.Dispatch<
    React.SetStateAction<GetAnalyticsPayslipsProcessedListParams | undefined>
  >;
  loading: boolean;
  employeeStatus: ReturnType<typeof useGetEmployeeStatus>['data'];
  taxPeriodPayments: ReturnType<typeof useGetPaymentsTaxPeriod>['data'];
  refresh: () => Promise<void>;
};

const DashboardEmployerAnalyticsContext =
  createContext<DashboardEmployerAnalyticsContextValue | null>(null);

export const DashboardEmployerAnalyticsProvider: React.FC<{
  children: React.ReactNode;
  initialParams?: GetAnalyticsPayslipsProcessedListParams;
}> = ({ children, initialParams }) => {
  const [params, setParams] = useState<GetAnalyticsPayslipsProcessedListParams | undefined>(
    initialParams,
  );

  const {
    data: employeeStatus,
    loading: employeeStatusLoading,
    fetchEmployeeStatus,
  } = useGetEmployeeStatus(params);

  const {
    data: taxPeriodPayments,
    loading: taxPeriodLoading,
    fetchPaymentsTaxPeriod,
  } = useGetPaymentsTaxPeriod(params);

  const refresh = useCallback(async () => {
    await Promise.all([fetchEmployeeStatus(), fetchPaymentsTaxPeriod()]);
  }, [fetchEmployeeStatus, fetchPaymentsTaxPeriod]);

  useEffect(() => {
    refresh();
  }, [params?.payFrequencies, params?.employerIds]);

  const value: DashboardEmployerAnalyticsContextValue = useMemo(
    () => ({
      params,
      setParams,
      loading: employeeStatusLoading || taxPeriodLoading,
      employeeStatus,
      taxPeriodPayments,
      refresh,
    }),
    [
      employeeStatus,
      employeeStatusLoading,
      params,
      setParams,
      refresh,
      taxPeriodLoading,
      taxPeriodPayments,
    ],
  );

  return (
    <DashboardEmployerAnalyticsContext.Provider value={value}>
      {children}
    </DashboardEmployerAnalyticsContext.Provider>
  );
};

export const useDashboardEmployerAnalytics =
  (): DashboardEmployerAnalyticsContextValue => {
    const context = useContext(DashboardEmployerAnalyticsContext);
    if (!context) {
      throw new Error(
        'useDashboardEmployerAnalytics must be used within DashboardEmployerAnalyticsProvider',
      );
    }
    return context;
  };
